import { Button, Center, Group, Stack } from "@mantine/core";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { AuctionsTableRow } from "../AuctionsTableRow";

import { EmptyState, ListTable, useColumnSorter } from "@/components/elements";
import { paths } from "@/config/paths";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { ListTableColumnSortBy, ListTableTh } from "@/components/elements";
import type { Auction, AuctionColumns } from "@/types/auctions";
import type { FC } from "react";

export type AuctionsTableProps = {
  auctions?: Auction[];
  columns?: AuctionColumns[];
  getPath?: (auctionId: string) => string;
  isSearching?: boolean;
  namespace?: string;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearch?: (query: string) => void;
  page?: number;
  pageSize?: number;
  totalAuctions?: number;
  disableNavigation?: boolean;
};

export const AuctionsTable: FC<AuctionsTableProps> = ({
  auctions = [],
  columns = [],
  getPath,
  isSearching = false,
  namespace = "auctions-table",
  onPageChange,
  onPageSizeChange,
  onSearch,
  page = 1,
  pageSize = 25,
  totalAuctions = 0,
  disableNavigation = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, getSortById, getUpdateSort } = useColumnSorter<Auction, AuctionColumns>(
    `${namespace}-sort`,
    undefined,
    {
      data: auctions,
      sortBy: sortDataBy,
    }
  );

  const { data: plans } = usePlansUser();

  const tableColumns: ListTableTh[] = columns.map((id) => ({
    id,
    label: t(`components.AuctionsTable.column.${id}`, id),
    sorter: id !== "num_cars" ? getColumnSorter(id) : undefined,
    thProps: {
      miw: "max-content",
      style: { whiteSpace: "nowrap", width: "1%" },
    },
  }));

  const hasPageSizeChanger = !!onPageSizeChange && totalAuctions > 10;
  const hasPagination = !!onPageChange && totalAuctions > pageSize;

  function getColumnSorter(id: AuctionColumns) {
    return (
      <ListTable.ColumnSorter
        tooltip={t("garage.table_sort_tooltip")}
        aria-label={t("garage.table_sort_aria_label", {
          column: t(`components.AuctionsTable.column.${id}`, id),
        })}
        sortBy={getSortById(id)}
        onSortChange={onChangeSortBy(id)}
      />
    );
  }

  function navigateToAuction(auctionId: string) {
    if (getPath) {
      const path = getPath(auctionId);
      navigate(path);
    }
  }

  function onChangeSortBy(id: AuctionColumns) {
    return (sortBy?: ListTableColumnSortBy) => {
      getUpdateSort(id)(sortBy);
    };
  }

  function sortDataBy(id: AuctionColumns, sortBy: ListTableColumnSortBy = "asc") {
    return (a: Auction, b: Auction) => {
      // DATE SORT
      if (id === "range_date") {
        const aValue = dayjs.utc(a.start_date).unix();
        const bValue = dayjs.utc(b.start_date).unix();

        return sortBy === "asc" ? aValue - bValue : bValue - aValue;
      }

      // LOTS SOLD (nested field)
      if (id === "total_value") {
        const aValue = a.event_analysis?.lots_sold ?? 0;
        const bValue = b.event_analysis?.lots_sold ?? 0;

        return sortBy === "asc" ? aValue - bValue : bValue - aValue;
      }

      // NUMBER OF LOTS (nested field)
      if (id === "number_of_lots") {
        const aValue = a.event_analysis?.number_of_lots ?? 0;
        const bValue = b.event_analysis?.number_of_lots ?? 0;

        return sortBy === "asc" ? aValue - bValue : bValue - aValue;
      }

      // GENERIC FIELDS
      const aValue = a[id as keyof Auction];
      const bValue = b[id as keyof Auction];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortBy === "asc" ? -1 : 1;
      if (bValue == null) return sortBy === "asc" ? 1 : -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortBy === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sortBy === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    };
  }

  return (
    <ListTable.Context columns={tableColumns} namespace={namespace}>
      {({ headingColumns }) => {
        const shouldShowEmptyState = data.length === 0 && plans?.name !== "starter";
        const headColumns = shouldShowEmptyState ? [] : headingColumns;
        const columnsId = headColumns.map(({ id }) => id) as AuctionColumns[];

        return (
          <>
            {onSearch && (
              <Group gap="md" py="lg">
                <ListTable.SearchInput
                  onChange={onSearch}
                  placeholder={t("components.AuctionsTable.search_placeholder")}
                  resetTooltip={t("components.AuctionsTable.reset_tooltip")}
                  updateOnChange
                />
              </Group>
            )}

            <ListTable
              data={{
                head: headColumns,
                body:
                  data.length > 0
                    ? data.map((auction) => (
                        <AuctionsTableRow
                          key={auction.id}
                          columns={columnsId}
                          data={auction}
                          onClick={
                            // Important: the UI may display 1 even when the actual value is 0.
                            // Navigation depends on the actual "number_of_lots" value.
                            !disableNavigation && (auction.event_analysis?.number_of_lots ?? 0) >= 1
                              ? () => navigateToAuction(auction.id)
                              : undefined
                          }
                          to={
                            !disableNavigation && (auction.event_analysis?.number_of_lots ?? 0) >= 1
                              ? getPath?.(auction.id)
                              : undefined
                          }
                        />
                      ))
                    : [],
              }}
              minWidth={Math.max(900, columns.length * 130)}
              tableProps={{ highlightOnHover: true }}
            />

            {data.length === 0 && plans?.name !== "starter" && (
              <Center maw={300} mx="auto" my="xl">
                <Stack align="center" gap="lg">
                  <EmptyState
                    type="noResults"
                    text={
                      isSearching
                        ? t("components.AuctionsTable.no_search_results")
                        : t("components.AuctionsTable.no_results")
                    }
                    gutter={0}
                  />
                  {!paths.app.auction.root.getHref() && (
                    <Button
                      component={Link}
                      to={paths.app.auction.root.getHref()}
                      aria-label={t("components.Auctions.all_auctions")}
                    >
                      {t("components.Auctions.all_actions")}
                    </Button>
                  )}
                </Stack>
              </Center>
            )}

            {(hasPageSizeChanger || hasPagination) && (
              <Group justify="flex-end" p="lg">
                {hasPageSizeChanger && (
                  <ListTable.PageSizeChanger
                    label={t("components.ListTable.page_size_changer_label")}
                    onChange={onPageSizeChange}
                    pageSize={pageSize}
                  />
                )}
                {hasPagination && (
                  <ListTable.Pagination
                    divider={t("components.ListTable.pagination_divider")}
                    page={page}
                    pageSize={pageSize}
                    onChange={onPageChange}
                    total={totalAuctions}
                  />
                )}
              </Group>
            )}
          </>
        );
      }}
    </ListTable.Context>
  );
};
